import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, MeResponse, UserSettings } from '@atnt/shared';
import { api, ApiError } from './client';

export const meQuery = queryOptions({
  queryKey: ['auth', 'me'],
  queryFn: async (): Promise<MeResponse | null> => {
    try {
      return await api.get<MeResponse>('/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 5 * 60_000,
});

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<MeResponse>('/auth/login', input),
    onSuccess: (me) => qc.setQueryData(meQuery.queryKey, me),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      qc.setQueryData(meQuery.queryKey, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    },
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<UserSettings>) => api.patch<UserSettings>('/auth/me/settings', patch),
    onSuccess: (settings) =>
      qc.setQueryData(meQuery.queryKey, (me) =>
        me ? { ...me, user: { ...me.user, settings, locale: settings.locale, theme: settings.theme } } : me,
      ),
  });
}
